import React, { createContext, useContext, useState } from "react";

// Définir une interface pour les questions utilisées par chaque équipe
interface TeamQuestions {
  [theme: string]: Record<number, Set<number>>; // Mapping de thème à difficulté à ensemble d'IDs de questions
}

// Définir le type du contexte
interface UsedQuestionsContextType {
  teamUsedQuestions: Record<string, TeamQuestions>;
  markTeamQuestionAsUsed: (
    teamId: number,
    theme: string,
    difficulty: number,
    questionId: number
  ) => void;
  resetTeamUsedQuestions: (teamId: number) => void;
}

// Créer le contexte
const QuestionsContext = createContext<UsedQuestionsContextType | undefined>(
  undefined
);

// Créer le hook pour utiliser le contexte
export const useQuestions = () => {
  const context = useContext(QuestionsContext);
  if (context === undefined) {
    throw new Error("useQuestions must be used within a QuestionsProvider");
  }
  return context;
};

interface QuestionsProviderProps {
  children: React.ReactNode;
}

// Créer le fournisseur de contexte
export const QuestionsProvider = ({ children }: QuestionsProviderProps) => {
  const [teamUsedQuestions, setTeamUsedQuestions] = useState<
    Record<string, TeamQuestions>
  >({});

  // Fonction pour marquer une question comme utilisée par une équipe
  const markTeamQuestionAsUsed = (
    teamId: number,
    theme: string,
    difficulty: number,
    questionId: number
  ) => {
    setTeamUsedQuestions((prev) => ({
      ...prev,
      [teamId]: {
        ...prev[teamId],
        [theme]: {
          ...prev[teamId]?.[theme],
          [difficulty]: new Set([
            ...(prev[teamId]?.[theme]?.[difficulty] ?? []),
            questionId,
          ]),
        },
      },
    }));
  };

  // Fonction pour réinitialiser les questions utilisées par une équipe
  const resetTeamUsedQuestions = (teamId: number) => {
    setTeamUsedQuestions((prevTeamUsedQuestions) => ({
      ...prevTeamUsedQuestions,
      [teamId]: {}, // Réinitialiser les questions utilisées pour cette équipe
    }));
  };

  return (
    <QuestionsContext.Provider
      value={{
        teamUsedQuestions,
        markTeamQuestionAsUsed,
        resetTeamUsedQuestions,
      }}
    >
      {children}
    </QuestionsContext.Provider>
  );
};
